import { AxiosError } from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { axiosGet, axiosPut, axiosPost, axiosPostV3 } from '../../api/axios-handler';
import { peersQueryKeys } from './usePeers';
import { experiencesQueryKeys } from './useExperiences';
import { useTagGroups } from './useTagGroups';
import { resourceKeys } from './useResources';
import { featureFlagKeys } from './useFeatureFlag';
import { analytics, EventTypes } from '../../utils/analytics';
import moment from 'moment';
import { StickyStateKeys } from '../utils/useStickyState';
import { resolveMwaKeycloakClientId } from '../../utils/mwa-keycloak-client-id';

export const userQueryKeys = {
  me: ['me'] as const,
  partial: ['partial'] as const,
};

export type TagFilters = {
  tag_group_ids: number[];
  tag_ids: number[];
};

export interface User {
  [key: string]: any;
  authorization_token?: string;
  access_role: any;
  administrator_role: any;
  administrator_role_id: any;
  api_key: string;
  caller_role: CallerRole;
  caller_role_id: number;
  password?: string;
  city: string;
  client_administrator_role: any;
  client_administrator_role_id: any;
  client_id: number;
  client_name: string;
  counseling_configuration: any;
  created_at: string;
  crisis_cooldown_until: string;
  current_app_version: string;
  date_of_birth: string;
  dialcare_status: DialcareStatus;
  revive_status?: ReviveStatus;
  display_name: string;
  display_name_rejected: boolean;
  email_address: string;
  family: string | null;
  first_name: string;
  gender: string | null;
  hide_payment_tiles: boolean;
  how_did_you_hear: any;
  has_peers: boolean;
  id: number;
  is_dependent: boolean;
  is_partial: boolean;
  is_text_compatible_phone: boolean;
  languages: string[];
  last_active_at: string;
  last_name: string;
  listener_role: ListenerRole;
  listener_role_id: number;
  masquerading: boolean;
  military_branch: any;
  military_role: any;
  mobile_phone: string;
  mobile_phone_verification_started_at: string;
  mobile_phone_verification_token: string;
  mobile_phone_verification_new_number: string;
  mobile_phone_verified: boolean;
  needs_password: boolean;
  password_reset_token: string;
  password_reset_token_created_at: string;
  pronoun: string | null;
  proposed_display_name: any;
  race: string | null;
  relationship: string | null;
  relationship_type: string;
  search_queries: SearchQueries[] | null;
  spirituality: any;
  state: string | null;
  status: string;
  street_address_1: string;
  street_address_2: string;
  test_user: boolean;
  timezone: string;
  user_subscription: UserSubscription;
  zip_code: string;

  onUnlimitedPlan?: boolean;
  onCappedPlan?: boolean;
  onMemberPayPlan?: boolean;
  onSelfPayPlan?: boolean;
}

interface PaymentData {
  available_minutes: number;
  plan_payment_type: 'CAPPED_ACCESS' | 'UNLIMITED_ACCESS' | 'MEMBER_PAY' | 'SELF_PAY';
  rate_per_minute: number;
  total_monthly_minutes: number;
}

interface MemberSignupRequest {
  email_address: string;
  package_code: string;
}

export interface MemberSignupResponse {
  caller_role: MemberSignupCallerRole;
  created_at: Date;
  email_address: string;
  id: number;
  mobile_phone: string;
}

export interface MemberSignupCallerRole {
  created_at: Date;
  id: number;
  listener_preferences: any;
}

export interface CallerRole {
  accepts_marketing_emails: boolean;
  created_at: string;
  crm_external_id: string | null;
  id: number;
  is_call_units?: boolean;
  listener_preferences: ListenerPreferences;
  notifications_check_ins: boolean;
  notifications_incoming_call: boolean;
  notifications_listener_online: boolean;
  notifications_resources: boolean;
  active_subscription?: PackageSubscription;
  package_subscriptions?: PackageSubscription[];
  status: string;
  stripe_customer_id: string;
  stripe_payment_failed: boolean;
  stripe_payment_failure_code: any;
  stripe_payment_method_id: string;
  tag_group_ids: number[] | null;
  tag_ids: number[] | null;
  topic_preferences?: { id: number; tag_group_id: number }[];
  total_available_credit: any;
  type: any;
  payment_data: PaymentData;
}

export interface ListenerPreferences {
  age_range: AgeRange;
  all_topics: boolean;
  genders: Genders;
  languages: string[];
  preferred_language: string;
  topic_tags: number[];
  topics: number[];
}

export interface AgeRange {
  from: number;
  to: number;
}

export interface Genders {
  female: boolean;
  male: boolean;
  other: boolean;
}

export interface PackageSubscription {
  created_at: string;
  start_date: string;
  end_date: Date;
  package: Package;
  package_id: number;
}

export interface Package {
  archived_at: any;
  benefits_description: string;
  branding_configuration: BrandingConfiguration;
  can_manage_dependents: boolean;
  cost_per_minute_cents: number;
  client: Client;
  code: string;
  collect_user_address: boolean;
  dependents_configuration: DependentsConfiguration;
  external_plan_id: number;
  force_web_app: any;
  id: number;
  my_plan_file_url: string;
  name: string;
  package_products: PackageProduct[];
  subscriptions: any[];
  theme_name: any;
}

export interface BrandingConfiguration {
  button_label: string;
  enabled: boolean;
  image_url: string;
  placements: string[];
  type: string;
  url: string;
  video_url: any;
}

interface Theme extends JSON {
  ['color-primary']: string;
  ['color-secondary']: string;
  ['color-buttonPrimary']: string;
  ['color-buttonSecondary']: string;
}

export const clientAIModels = ['simple_search', 'cohere_rerank', 'traditional_search'] as const;

// TODO this type is also defined in usePackageDetails.tsx and useClientCode.tsx.
// These should be consolidated into a single declaration.
export interface Client {
  id: number;
  accept_policy_description: string;
  ai_model: (typeof clientAIModels)[number];
  landing_page_content: any;
  logo_background_file_url: any;
  logo_file_url: string;
  comms_logo_file_url: string;
  logo_overlay_file_url: any;
  member_self_enrollment: boolean;
  name: string;
  promo_configuration: PromoConfiguration;
  sso_configuration?: any;
  video_configuration: VideoConfiguration;
  client_code: string;
  powered_by_header_file_url: string;
  powered_by_header_enabled: boolean;
  group_banner_file_url: string;
  group_banner_enabled: boolean;
  use_global_resources: boolean;
  theme?: Theme;
  landing_page_logo_file_url: string;
  landing_page_message: string;
  benefits_description: string;
  benefits_description_2: string;
  welcome_video_vimeo_id: string;
  use_mfa: boolean;
  trusted_sso: boolean;
}

export interface PromoConfiguration {
  button_label: string;
  image_url: string;
  placements: string[];
  type: string;
  url: string;
  video_url: string;
}

export interface VideoConfiguration {
  image_url: string;
  title: string;
  video_url: string;
}

export interface DependentsConfiguration {
  dependent_count: number;
  enabled: boolean;
  members_can_add_dependents: boolean;
}

export interface PackageProduct {
  configuration: Configuration;
  created_at: string;
  id: number;
  package_id: number;
  product: Product;
  product_id: number;
  status: string;
}

export interface Configuration {
  image_file?: string;
  required_points?: number;
  reward_value?: number;
  auto_renew_frequency?: string;
  engagement_points?: number;
  interim_question?: string;
  primary_question?: string;
  hide_payment_tiles?: boolean;
  maximum_minutes_per_user?: number;
}

export interface Product {
  id: number;
  key: string;
  product_type: string;
}

export interface DialcareStatus {
  has_address: boolean;
  processing_date: string;
}

export interface ReviveStatus {
  processing_date: string;
}

export interface ListenerRole {
  about_me: any;
  about_me_rejected: boolean;
  availability_changes_at: any;
  availability_changes_to: any;
  available: boolean;
  background_check_fee_collected: any;
  background_check_reward_applied: any;
  background_check_verified: boolean;
  background_traits: any[];
  call_rate: any;
  calls_succeeded: number;
  can_take_calls: boolean;
  checkr_id: any;
  completed_training: CompletedTraining;
  connection_blocked: boolean;
  connection_passes: number;
  connection_requests: number;
  created_at: string;
  first_active_listener_at: any;
  first_active_peer_at: any;
  has_completed_listener_guide: boolean;
  has_completed_peer_guide: boolean;
  has_lobby_subscription: boolean;
  has_required_experiences: boolean;
  has_required_office_hours: boolean;
  has_required_story: boolean;
  id: number;
  is_active: boolean;
  is_peer_active: boolean;
  is_reviewable: boolean;
  is_volunteer: boolean;
  last_tier_evaluation_at: string;
  military_tag: any;
  notifications_caller_online: boolean;
  notifications_incoming_call: boolean;
  notifications_resources: boolean;
  peer_status: string;
  premier_listener: boolean;
  primary_language: string;
  primary_language_tag: string;
  profile_photo_approved: boolean;
  profile_photo_file_url: string;
  profile_photo_modified_at: string;
  profile_photo_square_file_url: any;
  profile_traits: string[];
  proposed_about_me: any;
  refer_peer_org: any;
  refer_platform: any;
  required_training_complete: boolean;
  secondary_language: any;
  secondary_language_tag: string;
  share_link_url: string;
  spirituality: string;
  spirituality_tag: string;
  state: string;
  status: string;
  stripe_connect_state_token: any;
  stripe_customer_id: any;
  stripe_payment_intent_id: any;
  stripe_payment_method_id: any;
  stripe_user_id: any;
  tier: Tier;
  tier_id: number;
  type: any;
}

export interface CompletedTraining {}

export interface Tier {
  call_bonus: number;
  call_earnings: number;
  experience_bonus: number;
  experience_listen_rate: number;
  id: number;
  is_default: boolean;
  max_daily_office_hours: number;
  max_experience_payouts: number;
  minimum_office_hours: number;
  name: string;
  office_hour_rate: number;
  paid_office_hours_cap: number;
  pay_office_hours: boolean;
  sort_weight: number;
  status: string;
}

export interface SearchQueries {
  created_at: string;
  query: string;
}

export interface UserSubscription {
  created_at: string;
  id: number;
  remaining_minutes: number;
  status: string;
  subscribed_on_day: number;
  subscription: Subscription;
  subscription_id: number;
}

export interface Subscription {
  cents_per_minute: number;
  code: string;
  id: number;
  name: string;
}

export interface Authenticate {
  email_address: string;
  password: string;
  trusted: boolean;
}

interface PartialMemberRequest {
  email_address?: string;
  user_id?: number;
}

export interface PartialMember {
  user_id: number;
  user_email_obfuscated: string;
  user_phone_obfuscated: string;
  package_code: string;
}

const getUser = (): Promise<User> => {
  // Don't make API call if there's no auth token (user is logged out)
  const authToken = localStorage.getItem(StickyStateKeys.AuthorizationToken);
  if (!authToken) {
    return Promise.reject(new Error('No authentication token available'));
  }

  return axiosGet(`/users/me`, {})
    .then((userData) => {
      if (userData.data.user_id_hash) {
        analytics.trackEvent(EventTypes.SET_USER_ID, userData.data.user_id_hash);
      }
      if (userData.data?.caller_role && userData.data.caller_role.active_subscription) {
        const activeSubscription = userData.data.caller_role.active_subscription;
        const isTestUser = userData.data.test_user || activeSubscription.package.client.is_test;
        const age = Math.floor(moment().diff(userData.data.date_of_birth, 'years') / 10) * 10;
        const userDecade = `${age}s`;
        const clientCode = activeSubscription.package.client.client_code;
        analytics.trackEvent(
          EventTypes.SET_USER_DATA,
          activeSubscription.package.code,
          isTestUser,
          clientCode,
          userData?.data?.gender,
          age > 150 ? undefined : userDecade,
        );
      }

      return userData.data;
    })
    .catch((err: AxiosError) => {
      const data = err.response?.data as { error_code?: string; redirect_url?: string } | undefined;
      if (
        err.response?.status === 409 &&
        data?.error_code === 'WRONG_DOMAIN' &&
        data?.redirect_url &&
        typeof window !== 'undefined'
      ) {
        const target = data.redirect_url;
        try {
          const dest = new URL(target);
          const cur = new URL(window.location.href);
          if (cur.href !== dest.href) {
            window.location.assign(target);
          }
        } catch {
          window.location.assign(target);
        }
      }
      return Promise.reject(err);
    });
};

export const getPartialMember = (
  request: PartialMemberRequest,
  onSuccess: (data: PartialMember) => void,
  onError: (err: AxiosError) => void,
  onFinally?: () => void,
): void => {
  axiosGet(`/members/partial`, request)
    .then((partialMember) => onSuccess(partialMember.data))
    .catch((err: AxiosError) => {
      onError(err);
    })
    .finally(onFinally);
};

export const useUser = (enabled: boolean = true) => {
  const queryClient = useQueryClient();
  const { formatTagsAndTagGroups } = useTagGroups(false, enabled);
  const previousDataUpdatedAt = useRef<number | undefined>(undefined);

  const query = useQuery<User>(
    userQueryKeys.me,
    () => {
      return getUser();
    },
    {
      // refetch user every 30 minutes just to be safe. this could potentially be Infinity
      staleTime: 1000 * 60 * 30,

      //@TODO, useState is not immediately updated, so using for enabled here
      //results in retries against a known 403 response on logout
      enabled: enabled,
      select: (user) => {
        if (!user?.caller_role) {
          return user;
        }
        // Return a new object instead of mutating to maintain referential equality
        const packageSubscriptions =
          user.caller_role.package_subscriptions?.length === 0 ? undefined : user.caller_role.package_subscriptions;

        return {
          ...user,
          caller_role: {
            ...user.caller_role,
            package_subscriptions: packageSubscriptions,
          },
          onUnlimitedPlan: user?.caller_role.payment_data.plan_payment_type === 'UNLIMITED_ACCESS',
          onCappedPlan: user?.caller_role.payment_data.plan_payment_type === 'CAPPED_ACCESS',
          onMemberPayPlan: user?.caller_role.payment_data.plan_payment_type === 'MEMBER_PAY',
          onSelfPayPlan: user?.caller_role.payment_data.plan_payment_type === 'SELF_PAY',
          is_sso: !!user?.caller_role.active_subscription?.package.client.sso_configuration,
        };
      },
    },
  );

  // Only invalidate feature flags when we actually get new data, not on every query execution
  useEffect(() => {
    if (query.dataUpdatedAt && query.dataUpdatedAt !== previousDataUpdatedAt.current) {
      previousDataUpdatedAt.current = query.dataUpdatedAt;
      queryClient.invalidateQueries(featureFlagKeys.featureFlags);
    }
  }, [query.dataUpdatedAt, queryClient]);

  const updateTagPreferences = useMutation(
    ({ tag_group_ids, tag_ids }: TagFilters) => {
      return axiosPut(`/users/${query.data?.id}`, {
        caller_role: {
          ...formatTagsAndTagGroups(tag_group_ids, tag_ids),
        },
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(userQueryKeys.me);
        queryClient.invalidateQueries({ queryKey: peersQueryKeys.favorites });
        queryClient.invalidateQueries({ queryKey: experiencesQueryKeys.recommended });
        queryClient.invalidateQueries({ queryKey: resourceKeys.all });
        queryClient.removeQueries({ queryKey: experiencesQueryKeys.infiniteRecommended });
        queryClient.removeQueries({ queryKey: peersQueryKeys.infiniteRecommended });
        queryClient.invalidateQueries({ queryKey: peersQueryKeys.infiniteRecommended });
        queryClient.removeQueries(['textSearchExperiences']);
        queryClient.cancelQueries(['textSearchExperiences']);
      },
      onMutate: ({ tag_group_ids, tag_ids }) => {
        queryClient.setQueryData(userQueryKeys.me, (previousUser: any) => {
          return {
            ...previousUser,
            caller_role: {
              ...previousUser.caller_role,
              ...formatTagsAndTagGroups(tag_group_ids, tag_ids),
            },
          };
        });
      },
    },
  );

  const updateUser = useMutation(
    (input: Partial<User>) => {
      const userId = queryClient.getQueryData<User>(userQueryKeys.me)?.id ?? query.data?.id;
      if (!userId) {
        return Promise.reject(new Error('User profile is not loaded yet'));
      }
      return axiosPut(`/users/${userId}`, {
        ...input,
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['me']);
        queryClient.invalidateQueries(['paymentMethod']);
        queryClient.invalidateQueries(['stripeKeys']);
      },
    },
  );

  return {
    ...query,
    updateTagPreferences,
    updateUser,
  };
};

export const useSignupUser = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ email_address, package_code }: MemberSignupRequest) => {
      return axiosPost(
        '/members/',
        {
          email_address,
          package_code,
        },
        'v3',
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(userQueryKeys.me);
      },
    },
  );
};

export const useSendKeycloakVerificationLink = () =>
  useMutation(({ userId }: { userId: number }) => {
    return axiosPost(
      `/users/${userId.toString()}/keycloak_verification`,
      {
        app: 'mwa',
        keycloak_client: resolveMwaKeycloakClientId(),
      },
      'v3',
    );
  });

export const useSendKeycloakVerificationCode = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ mobilePhone }: { mobilePhone: string }) => {
      return axiosPost(`/users/send_phone_verification`, { new_phone_number: mobilePhone }, 'v3');
    },
    {
      onSuccess: (_, variables) => {
        // Track the pending number only — do not update mobile_phone until the OTP is verified.
        // Writing mobile_phone here caused Profile settings to hide the OTP step (KINDLY-1309).
        queryClient.setQueryData(userQueryKeys.me, (oldUser: any) => {
          if (!oldUser) return oldUser;
          return {
            ...oldUser,
            mobile_phone_verification_new_number: variables.mobilePhone,
          };
        });
      },
    },
  );
};

export const useVerifyKeycloakVerificationCode = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ verificationCode: verification_code }: { verificationCode: string }) => {
      return axiosPost(`/users/verify_phone_code`, { verification_code }, 'v3');
    },
    {
      retry: false,
      onSuccess: () => {
        queryClient.setQueryData(userQueryKeys.me, (oldUser: any) => {
          if (!oldUser) return oldUser;
          const verifiedPhone = oldUser.mobile_phone_verification_new_number || oldUser.mobile_phone;
          return {
            ...oldUser,
            mobile_phone: verifiedPhone,
            mobile_phone_verification_new_number: null,
            mobile_phone_verified: true,
          };
        });
        queryClient.invalidateQueries(userQueryKeys.me);
      },
    },
  );
};
